import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AnimateModule, categoriesFilterByBrand, TrainingBrand, TrainingFormat } from '@pro-connect/shared';
import { format } from 'date-fns';
import { isEqual } from 'lodash-es';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { plLocale } from 'ngx-bootstrap/locale';
import { tap } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IPlace } from '../../../../models/place';
import { ISubCategory } from '../trainings-categories/trainings-categories.component';

defineLocale('pl', plLocale);
export type EventsFilterFormType = {
  category_id: FormControl<number | null>;
  format: FormControl<TrainingFormat | null>;
  brand: FormControl<TrainingBrand | null>;
  place_id: FormControl<number | null>;
  educator_id: FormControl<number | null>;
  price_from: FormControl<number | null>;
  price_to: FormControl<number | null>;
  price_from_txt: FormControl<number | null>;
  price_to_txt: FormControl<number | null>;
  date_from: FormControl<Date | null>;
  date_to: FormControl<Date | null>;
  sort: FormControl<string | null>;
  direction: FormControl<'desc' | 'asc' | null>;
  city: FormControl<string | null>;
  sub_category_id: FormControl<number | null>;
  payment: FormControl<boolean | null>;
};

@Component({
  selector: 'app-trainings-filter',
  templateUrl: './trainings-filter.component.html',
  styleUrls: ['./trainings-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, AnimateModule, ReactiveFormsModule, BsDatepickerModule, NgSelectModule, FormsModule],
})
export class TrainingsFilterComponent implements OnInit {
  public readonly categories = input.required<{ id: number; name: string }[]>();
  public readonly subCategories = input.required<ISubCategory[]>();
  public readonly places = input.required<IPlace[]>();
  public readonly cities = input.required<string[]>();
  public readonly educators = input.required<any[]>();
  public readonly isBrandFilterVisible = input<boolean>(true);
  public readonly isCategoriesFilterVisible = input<boolean>(true);
  public readonly categoryId = input<number | null>(null);
  public readonly subCategoryId = input<number | null>(null);
  public readonly sortTable = input<{ value: string; name: string }[]>([]);
  public readonly filtersChanged = output<{
    category_id: number | null;
    format: TrainingFormat | null;
    brand: TrainingBrand | null;
    place_id: number | null;
    educator_id: number | null;
    price_from: number | null;
    price_to: number | null;
    date_from: string | null;
    date_to: string | null;
    sort: string | undefined;
    direction: 'desc' | 'asc' | undefined;
    city: string | null;
    sub_category_id: number | null;
    payment: 'free' | 'paid' | null;
  }>();
  public readonly filterFormGroup: FormGroup<EventsFilterFormType>;
  protected readonly TrainingBrand = TrainingBrand;
  public readonly brand = input<TrainingBrand | null>(null);
  public readonly maxRangeValue = input.required<number | null>();
  public minDate = new Date();
  public minDateMax = signal(new Date(format(new Date(), 'yyyy-mm-dd')));
  public readonly categoriesByBrand = computed<ISubCategory[]>(() => {
    return categoriesFilterByBrand(this.subCategories(), this.brand());
  });

  get f() {
    return this.filterFormGroup.controls;
  }

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _destroyRef: DestroyRef,
    private _bsLocaleService: BsLocaleService,
  ) {
    this.filterFormGroup = this._formBuilder.group<EventsFilterFormType>({
      category_id: new FormControl<number | null>(null, []),
      format: new FormControl<TrainingFormat | null>(null, []),
      brand: new FormControl<TrainingBrand | null>(null, []),
      place_id: new FormControl<number | null>(null, []),
      educator_id: new FormControl<number | null>(null, []),
      price_from: new FormControl<number | null>(0, []),
      price_to: new FormControl<number | null>(0, []),
      price_from_txt: new FormControl<number | null>(null, []),
      price_to_txt: new FormControl<number | null>(null, []),
      date_from: new FormControl<Date | null>(null, []),
      date_to: new FormControl<Date | null>(null, []),
      sort: new FormControl<string | null>(null, []),
      direction: new FormControl<'desc' | 'asc' | null>(null, []),
      city: new FormControl<string | null>(null, []),
      sub_category_id: new FormControl<number | null>(null, []),
      payment: new FormControl<boolean | null>(false, []),
    });
    effect(() => {
      this.filterFormGroup.controls['category_id'].setValue(this.categoryId());
      this.filterFormGroup.controls['sub_category_id'].setValue(this.subCategoryId());
      // this.filterFormGroup.controls['price_to'].setValue(this.maxRangeValue());
      // this.filterFormGroup.controls['price_to_txt'].setValue(this.maxRangeValue());
    });

    this._bsLocaleService.use('pl');
  }

  public ngOnInit(): void {
    this.filterFormGroup.valueChanges
      .pipe(
        debounceTime(300),
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(isEqual),
        tap((value) => {
          const priceTo =
            value.price_to_txt === null
              ? null
              : value.price_to != value.price_to_txt
                ? value.price_to_txt
                : value.price_to;
          const priceFrom =
            value.price_from_txt === null
              ? null
              : value.price_from !== value.price_from_txt
                ? value.price_from_txt
                : value.price_from;
          console.warn();
          if (
            Number(value.price_to) !== Number(value.price_to_txt) ||
            Number(value.price_from) !== Number(value.price_from_txt) ||
            (value.payment && (value.price_to_txt !== null || value.price_from_txt !== null))
          ) {
            return;
          }

          this.filtersChanged.emit({
            brand: value.brand ? value.brand : null,
            category_id: value.category_id ? value.category_id : null,
            place_id: value.place_id ? value.place_id : null,
            educator_id: value.educator_id ? value.educator_id : null,
            format: value.format ? value.format : null,
            price_from: priceFrom !== null ? priceFrom : null,
            price_to: priceTo !== null ? priceTo : null,
            date_from: value.date_from ? format(new Date(value.date_from), 'yyyy-MM-dd') : null,
            date_to: value.date_to ? format(new Date(value.date_to), 'yyyy-MM-dd') : null,
            sort: value.sort ? value.sort : undefined,
            direction: value.direction ? value.direction : undefined,
            city: value.city ? value.city : null,
            sub_category_id: value.sub_category_id ? value.sub_category_id : null,
            payment: value.payment ? 'free' : null,
          });

          if (value.date_from) {
            this.minDateMax.set(new Date(format(new Date(value.date_from), 'MM/dd/yyyy')));
          }
        }),
      )
      .subscribe();

    this.f['price_from_txt'].valueChanges
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
        tap((value: any) => {
          if (Number(this.f['price_from'].value) !== Number(value)) {
            this.f['price_from'].setValue(Number(value));
          }
          if (value && this.f.payment.value) {
            this.f.payment.setValue(null);
          }
        }),
      )
      .subscribe();

    this.f['price_to_txt'].valueChanges
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
        tap((value: any) => {
          if (Number(this.f['price_to'].value) !== Number(value)) {
            this.f['price_to'].setValue(Number(value));
          }
          if (value && this.f.payment.value) {
            this.f.payment.setValue(null);
          }
        }),
      )
      .subscribe();

    this.f['payment'].valueChanges
      .pipe(
        debounceTime(200),
        takeUntilDestroyed(this._destroyRef),
        tap((value: boolean | null) => {
          if (value) {
            this.f['price_to_txt'].setValue(null);
            this.f['price_from_txt'].setValue(null);
            this.f['price_to_txt'].updateValueAndValidity();
            this.f['price_from_txt'].updateValueAndValidity();
          }
        }),
      )
      .subscribe();

    this._changeNullifySubCategoryOnCategoryChange();
  }

  public unMarkRadio(formControl: FormControl, value: any): void {
    if (formControl.value === value) {
      return;
    }
    formControl.setValue(null);
  }

  public priceFromChange(): void {
    if (Number(this.f['price_from'].value) > Number(this.f['price_to'].value)) {
      this.f['price_to'].setValue(Number(this.f['price_from'].value));
    }
    this.f['price_from_txt'].setValue(Number(this.f['price_from'].value));
    this.f['price_to_txt'].setValue(Number(this.f['price_to'].value));
  }

  public priceToChange(): void {
    if (Number(this.f['price_to'].value) < Number(this.f['price_from'].value)) {
      this.f['price_from'].setValue(Number(this.f['price_to'].value));
    }
    this.f['price_from_txt'].setValue(Number(this.f['price_from'].value));
    this.f['price_to_txt'].setValue(Number(this.f['price_to'].value));
  }

  public sortDirectionChange(): void {
    // if (this.f['direction'].value === 'asc') {
    //   this.f['direction'].setValue('desc');
    // } else if (this.f['direction'].value === 'desc') {
    //   this.f['direction'].setValue(null);
    // } else if (this.f['direction'].value === null) {
    //   this.f['direction'].setValue('asc');
    // }
  }

  private _changeNullifySubCategoryOnCategoryChange(): void {
    this.f['category_id'].valueChanges
      .pipe(
        debounceTime(200),
        takeUntilDestroyed(this._destroyRef),
        tap((catId: number | null) => {
          this.f['sub_category_id'].setValue(null);
        }),
      )
      .subscribe();
  }
}
